import { useState, useEffect, useMemo, useContext } from "react"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { DataSourceOption } from "./DataSourcesFilter"
import { UserContext } from "src/providers"
import { useCanModifyDataSource } from "src/utils/useCanModifyDataSource"

interface UseDataSourceFilterReturn {
  /** The current data source filter */
  dataSourceFilter: DataSourceOption | undefined
  /** Function to update the data source filter */
  setDataSourceFilter: (value: DataSourceOption) => void
  /** Available data source options */
  dataSourceOptions: DataSourceOption[]
  /** Whether the user can modify (is admin of) the currently selected data source */
  canModify: boolean
}

export const useDataSourceFilter = (): UseDataSourceFilterReturn => {
  const { userCommunities } = useContext(UserContext)
  // Compute data source options from user communities
  const dataSourceOptions = useMemo<DataSourceOption[]>(
    () =>
      userCommunities?.map((community) => ({
        label: community.gatedCommunityName,
        value: community.gatedCommunityId
      })) || [],
    [userCommunities]
  )

  const [persistedFilter, setPersistedFilter] = useLocalStorage<DataSourceOption | undefined>(
    "dataSourcesDataSourceFilter",
    undefined
  )

  const [dataSourceFilter, setDataSourceFilterInternal] = useState<DataSourceOption | undefined>(
    persistedFilter || dataSourceOptions[0]
  )

  // Update the filter when options change (e.g., when userCommunities loads/changes)
  useEffect(() => {
    if (!dataSourceFilter && dataSourceOptions.length > 0) {
      // If no filter is set and we have options, set to first option
      setDataSourceFilterInternal(dataSourceOptions[0])
    } else if (dataSourceFilter && dataSourceOptions.length > 0) {
      // Check if the current filter still exists in the new options
      const stillExists = dataSourceOptions.some((opt) => opt.value === dataSourceFilter.value)
      if (!stillExists) {
        // If the current filter no longer exists, reset to first option
        setDataSourceFilterInternal(dataSourceOptions[0])
      }
    }
  }, [dataSourceOptions, dataSourceFilter])

  const setDataSourceFilter = (value: DataSourceOption) => {
    setDataSourceFilterInternal(value)
    setPersistedFilter(value)
  }

  // Check if user can modify the currently selected data source
  const canModify = useCanModifyDataSource(dataSourceFilter?.value)

  return {
    dataSourceFilter,
    setDataSourceFilter,
    dataSourceOptions,
    canModify
  }
}
